import { Entity } from '@common/types/Entity';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { theme } from '@prophecy/theme';
import { message, Stack, StackItem } from '@prophecy/ui-v3';
import { Text } from '@prophecy/ui-v3/Typography';
import { getFrozenEmptyObject } from '@prophecy/utils/constants';
import { formattedDateTime } from '@prophecy/utils/date';
import { useQuery } from '@tanstack/react-query';
import { getAboutInfo } from 'frontend/core/src/data/apis/api';
import { useGraphQlQuery } from 'frontend/core/src/data/util';
import { NestedData } from 'frontend/core/src/utils/types';
import { ResultOf } from 'gql.tada';
import { noop } from 'lodash-es';
import { useMemo } from 'react';
import styled from 'styled-components';

import { TeamMembersQuery } from '../Users/api';
import { UserProvider, useUserContext } from '../Users/context';
import { InviteUsers } from '../Users/InviteUsers';
import { UserList } from '../Users/UserList';
import { WorkspaceName } from './WorkspaceName';

const WorkspaceData = () => {
  const { currentWorkspace, currentWorkspaceId } = useUserWorkspace();

  const { canInvite } = useUserContext();

  const {
    data: { Team: teamData } = getFrozenEmptyObject<ResultOf<typeof TeamMembersQuery>>(),
    isLoading: isWorkspaceMembersLoading,
    invalidate
  } = useGraphQlQuery(
    [Entity.Team, 'teamMembers'],
    TeamMembersQuery,
    { enabled: Boolean(currentWorkspaceId) },
    { uid: currentWorkspaceId! }
  );

  const memberList = useMemo(() => {
    const { admins, members } = teamData || { admins: [], members: [] };
    const adminIdSet = new Set(admins.map((admin) => admin._id));
    return members.map((member) => ({
      ...member,
      isAdmin: adminIdSet.has(member._id)
    }));
  }, [teamData]);

  const ownerEmail = useMemo(() => {
    const ownerId = currentWorkspace?.createdBy;
    if (!ownerId) return '';
    return teamData?.members?.find((member) => member._id === ownerId)?.email ?? '';
  }, [currentWorkspace?.createdBy, teamData?.members]);

  const { data: aboutData } = useQuery<{ data: { version: string } }, NestedData>(['About'], () => getAboutInfo(), {
    retry: false,
    onError: (err) => {
      message.error({
        content: err?.message,
        detail: err?.trace,
        'data-test-id': 'workspace-info-about-error-message'
      });
    }
  });

  return (
    <StyledStack gap={theme.spaces.x32} height='100%'>
      <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center' align='space-between'>
        <WorkspaceName />
        {canInvite && <InviteUsers invalidateUserList={invalidate} teamId={currentWorkspaceId} />}
      </Stack>
      <Stack direction='vertical' gap={theme.spaces.x16}>
        <Text level='sm' weight={theme.fontWeight.semiBold}>
          About
        </Text>
        <AboutDataWrapper direction='horizontal' gap={theme.spaces.x64}>
          <Stack direction='vertical' gap={theme.spaces.x12}>
            <Text level='sm' tone={theme.colors.gray500}>
              Owner
            </Text>
            <Stack direction='horizontal' gap={theme.spaces.x4}>
              <Text level='xs' weight={theme.fontWeight.semiBold}>
                {currentWorkspace?.createdByFullName || ''}
              </Text>
              <Text level='xs' tone={theme.colors.gray500} weight={theme.fontWeight.normal}>
                ({ownerEmail})
              </Text>
            </Stack>
          </Stack>
          <Stack direction='vertical' gap={theme.spaces.x12}>
            <Text level='sm' tone={theme.colors.gray500}>
              Created At
            </Text>
            <Text level='xs' weight={theme.fontWeight.semiBold}>
              {formattedDateTime(currentWorkspace?.created ?? '', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </Text>
          </Stack>
          <Stack direction='vertical' gap={theme.spaces.x12}>
            <Text level='sm' tone={theme.colors.gray500}>
              Prophecy IDE Version
            </Text>
            <Text level='xs' weight={theme.fontWeight.semiBold}>
              {aboutData?.data?.version}
            </Text>
          </Stack>
        </AboutDataWrapper>
      </Stack>

      <Stack direction='vertical' align='space-between' gap={theme.spaces.x16}>
        <Text level='sm' weight={theme.fontWeight.semiBold}>
          Users
        </Text>
        <StyledStackItem grow='1'>
          <UserList
            isMemberMode
            userList={memberList}
            isLoading={isWorkspaceMembersLoading}
            invalidateUserList={invalidate}
            invalidatePendingRequests={noop}
            team={teamData}
          />
        </StyledStackItem>
      </Stack>
    </StyledStack>
  );
};

export const WorkspaceInfo = () => {
  const { currentWorkspaceId } = useUserWorkspace();

  return (
    <UserProvider teamId={currentWorkspaceId}>
      <WorkspaceData />
    </UserProvider>
  );
};

const StyledStackItem = styled(StackItem)`
  min-height: 400px;
`;

const StyledStack = styled(Stack)`
  margin-right: ${theme.spaces.x12};
`;

const AboutDataWrapper = styled(Stack)`
  background-color: ${theme.colors.white};
  border-radius: ${theme.radius.x12};
  padding: ${theme.spaces.x16};
  border: 1px solid ${theme.colors.gray200};
`;
